import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  School as SchoolIcon, 
  EmojiEvents as TrophyIcon,
  Calculate as CalculateIcon,
  Speed as SpeedIcon,
  Diversity3 as TeamIcon,
  AutoGraph as GraphIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';

function LandingPage() {
  const theme = useTheme();
  // Mobile detection for responsive design
  useMediaQuery(theme.breakpoints.down('sm'));

  // Features data
  const features = [
    {
      icon: <CalculateIcon fontSize="large" />,
      title: 'Math Challenges',
      description: 'Engaging problems that develop critical thinking and problem-solving skills.'
    },
    {
      icon: <TeamIcon fontSize="large" />,
      title: 'Build Community',
      description: 'Connect with other math enthusiasts and learn together in a supportive environment.'
    },
    {
      icon: <GraphIcon fontSize="large" />,
      title: 'Track Progress',
      description: 'Monitor your improvement over time with detailed performance analytics.'
    },
    {
      icon: <TrophyIcon fontSize="large" />,
      title: 'Win Rewards',
      description: 'Earn recognition and prizes for your mathematical achievements.'
    },
    {
      icon: <SchoolIcon fontSize="large" />,
      title: 'Academic Growth',
      description: 'Enhance your mathematical skills beyond the standard curriculum.'
    },
    {
      icon: <SpeedIcon fontSize="large" />,
      title: 'Compete Live',
      description: 'Join real-time competitions that test speed and accuracy under pressure.'
    }
  ];

  return (
    <MainLayout>
      {/* Hero Section */}
      <Box 
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #3f51b5 0%, #303f9f 60%, #1a237e 100%)',
          color: 'white',
          borderRadius: '16px',
          overflow: 'hidden',
          mb: 10,
          py: { xs: 6, md: 8 },
          px: { xs: 2, sm: 4, md: 6 },
          boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)'
        }}
      >
        {/* Abstract math background pattern */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.08,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '100px 100px',
          }}
        />
        
        {/* Radial gradient overlay */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 60%)',
          }}
        />
        
        <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={7}>
            {/* Mr. T Presents text */}
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                mb: 1
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontStyle: 'italic',
                  fontWeight: 500,
                  opacity: 0.9,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  background: 'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,223,91,1) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block'
                }}
              >
                Mr. T Presents:
              </Typography>
            </Box>
            
            {/* Main Title with animation */}
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                lineHeight: 1.1,
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                background: 'linear-gradient(90deg, #ffffff 0%, #e8eaf6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -10,
                  left: 0,
                  width: 120,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: theme.palette.secondary.main
                }
              }}
            >
              MathWiz League
            </Typography>
            
            <Typography 
              variant="h5" 
              component="h2" 
              sx={{ 
                mb: 4, 
                mt: 3,
                opacity: 0.95,
                fontWeight: 500,
                maxWidth: 600,
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              Challenge your math skills in our middle school competition
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4, 
                maxWidth: 600,
                fontSize: '1.1rem',
                lineHeight: 1.6,
                fontWeight: 400,
                textShadow: '0 1px 1px rgba(0,0,0,0.1)'
              }}
            >
              Join our community of young mathematicians and compete in 
              exciting math challenges. Develop problem-solving skills, build confidence,
              and have fun with math outside of the classroom!
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 3, 
                flexWrap: 'wrap',
                mt: 4,
                position: 'relative'
              }}
            >
              <Button 
                variant="contained" 
                color="secondary" 
                size="large"
                component={RouterLink}
                to="/register"
                sx={{ 
                  px: 4, 
                  py: 2,
                  borderRadius: '30px',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 25px rgba(0,0,0,0.25)'
                  }
                }}
              >
                Register Now
              </Button>
              <Button 
                variant="outlined" 
                color="inherit"
                size="large"
                component={RouterLink}
                to="/login"
                sx={{ 
                  px: 4, 
                  py: 2,
                  borderRadius: '30px',
                  borderWidth: 2,
                  borderColor: 'rgba(255,255,255,0.7)',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-3px)'
                  }
                }}
              >
                Sign In
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'relative' }}>
              {/* Decorative elements */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,193,7,0.3) 0%, rgba(255,193,7,0) 70%)',
                  zIndex: 1
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  left: -20,
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(76,175,80,0.3) 0%, rgba(76,175,80,0) 70%)',
                  zIndex: 1
                }}
              />
              
              {/* Custom math-themed illustration */}
              <Box
                sx={{
                  width: '100%',
                  height: 400,
                  borderRadius: '12px',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
                  border: '4px solid rgba(255,255,255,0.15)',
                  position: 'relative',
                  zIndex: 2,
                  transition: 'transform 0.3s ease',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  background: 'linear-gradient(135deg, #303f9f 0%, #1a237e 100%)',
                  padding: 3,
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
              >
                {/* Math-themed decorative elements */}
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%',
                  opacity: 0.1,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M29 0h1v60h-1zM59 29v1H0v-1z'/%3E%3C/g%3E%3C/svg%3E")`
                }} />
                
                {/* Math symbols */}
                <Typography 
                  variant="h1" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.1)', 
                    fontSize: '120px', 
                    fontWeight: 'bold',
                    position: 'absolute',
                    top: '10%',
                    left: '10%'
                  }}
                >
                  +
                </Typography>
                <Typography 
                  variant="h1" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.1)', 
                    fontSize: '140px', 
                    fontWeight: 'bold',
                    position: 'absolute',
                    bottom: '5%',
                    right: '10%'
                  }}
                >
                  ÷
                </Typography>
                <Typography 
                  variant="h1" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.1)', 
                    fontSize: '100px', 
                    fontWeight: 'bold',
                    position: 'absolute',
                    bottom: '30%',
                    left: '15%'
                  }}
                >
                  −
                </Typography>
                <Typography 
                  variant="h1" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.1)', 
                    fontSize: '120px', 
                    fontWeight: 'bold',
                    position: 'absolute',
                    top: '25%',
                    right: '15%'
                  }}
                >
                  ×
                </Typography>
                <Typography 
                  variant="h1" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.1)', 
                    fontSize: '80px', 
                    fontWeight: 'bold',
                    position: 'absolute',
                    top: '60%',
                    left: '35%'
                  }}
                >
                  π
                </Typography>
                
                {/* Central content */}
                <CalculateIcon 
                  sx={{ 
                    fontSize: '5rem', 
                    color: 'white',
                    mb: 2
                  }} 
                />
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    textAlign: 'center',
                    textShadow: '0 2px 5px rgba(0,0,0,0.3)'
                  }}
                >
                  Learn. Compete. Excel.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      
      {/* Features Section */}
      <Box 
        sx={{ 
          mb: 12,
          position: 'relative',
          py: 4
        }}
      >
        {/* Background decorative elements */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: -100,
            right: -100,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(63,81,181,0.06) 0%, rgba(63,81,181,0) 70%)',
            zIndex: -1
          }}
        />
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(156,39,176,0.05) 0%, rgba(156,39,176,0) 70%)',
            zIndex: -1
          }}
        />
      
        <Typography 
          variant="h3" 
          component="h2" 
          align="center"
          sx={{
            fontWeight: 800,
            color: 'primary.main',
            position: 'relative',
            display: 'inline-block',
            margin: '0 auto',
            mb: 1,
            width: '100%',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 80,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.secondary.main
            }
          }}
        >
          What We Offer
        </Typography>
        
        <Typography 
          variant="h6" 
          align="center" 
          sx={{ 
            mb: 8, 
            maxWidth: 800, 
            mx: 'auto',
            mt: 4,
            color: '#546e7a',
            fontWeight: 400,
            lineHeight: 1.6,
            fontSize: '1.1rem'
          }}
        >
          MathWiz League provides engaging challenges and a supportive community
          to help middle school students excel in mathematics.
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => {
            // Generate different accent colors for each feature
            const colors = [
              { bg: '#3f51b5', light: 'rgba(63, 81, 181, 0.08)', mid: 'rgba(63, 81, 181, 0.12)' },
              { bg: '#f44336', light: 'rgba(244, 67, 54, 0.08)', mid: 'rgba(244, 67, 54, 0.12)' },
              { bg: '#009688', light: 'rgba(0, 150, 136, 0.08)', mid: 'rgba(0, 150, 136, 0.12)' },
              { bg: '#ff9800', light: 'rgba(255, 152, 0, 0.08)', mid: 'rgba(255, 152, 0, 0.12)' },
              { bg: '#9c27b0', light: 'rgba(156, 39, 176, 0.08)', mid: 'rgba(156, 39, 176, 0.12)' },
              { bg: '#2196f3', light: 'rgba(33, 150, 243, 0.08)', mid: 'rgba(33, 150, 243, 0.12)' }
            ];
            const color = colors[index % colors.length];
            
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  elevation={4}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    border: '1px solid rgba(0,0,0,0.08)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 15px 30px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      background: `linear-gradient(135deg, ${color.light} 0%, ${color.mid} 100%)`,
                      borderBottom: '1px solid rgba(0,0,0,0.05)'
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: color.bg,
                        color: 'white',
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                        mb: 2,
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography 
                      variant="h5" 
                      component="div" 
                      sx={{ 
                        fontWeight: 700,
                        fontSize: '1.4rem',
                        color: '#37474f',
                        textAlign: 'center'
                      }}
                    >
                      {feature.title}
                    </Typography>
                  </Box>
                  <CardContent 
                    sx={{ 
                      flexGrow: 1,
                      p: 3,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#546e7a',
                        lineHeight: 1.6,
                        textAlign: 'center',
                        fontWeight: 400
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      
      {/* Testimonials Section */}
      <Box sx={{ mb: 8 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          align="center"
          fontWeight="bold"
          color="primary"
          gutterBottom
        >
          Success Stories
        </Typography>
        <Typography 
          variant="h6" 
          align="center" 
          color="text.secondary"
          sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}
        >
          Hear what students and parents are saying about their experience with MathWiz League.
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 3, 
                height: '100%',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="body1" gutterBottom sx={{ fontStyle: 'italic', mb: 2 }}>
                "Mr. T inspired my love for math in grade school! From then on, my interest in math carried on 
                into high school where I grew to love calculus and physics. I majored in aerospace engineering 
                and am now an F/A-18 & EA-18G Flight Test Engineer at Boeing. Mr. T's math class both truly 
                inspired me, leading me to a dream job working on fighter jets!"
              </Typography>
              <Box sx={{ mt: 'auto' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Christine Moeller
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Former Student of Mr. T
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 3, 
                height: '100%',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="body1" gutterBottom sx={{ fontStyle: 'italic', mb: 2 }}>
                "My son did Mr. T's math league last summer. The problems were challenging but he was able 
                to use the videos and materials to find patterns and tricks for solving them. He had a lot 
                of fun keeping track of his "world ranking." The program helped him increase his confidence 
                in math and he even picked up a little hardware at a regional math contest recently. 
                (Seen here with Mr. T after the competition)"
              </Typography>
              <Box sx={{ mt: 'auto' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Jenny Kreimer Bedel
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Parent of 7th Grader, Cincinnati
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 3, 
                height: '100%',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="body1" gutterBottom sx={{ fontStyle: 'italic', mb: 2 }}>
                "The competitive yet supportive environment at MathWiz League is perfect for
                students who want to push themselves beyond the standard curriculum."
              </Typography>
              <Box sx={{ mt: 'auto' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Sarah Williams
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Math Teacher
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      
      {/* CTA Section */}
      <Box 
        sx={{
          position: 'relative',
          backgroundImage: 'linear-gradient(135deg, #673ab7 0%, #512da8 100%)',
          borderRadius: '16px',
          p: { xs: 4, sm: 6, md: 8 },
          mb: 10,
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.12)'
        }}
      >
        {/* Background patterns and decorative elements */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            backgroundSize: '180px 180px',
          }}
        />
        
        {/* Light beam effect */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '90%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 60%)',
            transform: 'rotate(35deg)',
            zIndex: 0
          }}
        />
        
        <Grid 
          container 
          spacing={6} 
          alignItems="center" 
          sx={{ 
            position: 'relative',
            zIndex: 1 
          }}
        >
          <Grid item xs={12} md={7}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{
                fontWeight: 800,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '2.8rem' },
                textShadow: '0 2px 15px rgba(0,0,0,0.2)'
              }}
            >
              Ready to Start Your Math Journey?
            </Typography>
            <Typography 
              variant="h6"
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                mb: 3,
                fontWeight: 400,
                fontSize: '1.1rem',
                lineHeight: 1.5,
                maxWidth: 600
              }}
            >
              Join MathWiz League today and take your math skills to the next level! Our engaging challenges and supportive community are waiting for you.
            </Typography>
            
            {/* Feature bullets */}
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mb: { xs: 4, md: 0 }
              }}
            >
              {[
                'Access to weekly math competitions',
                'Personalized progress tracking',
                'Recognition for your achievements',
                'Connect with fellow math enthusiasts'
              ].map((item, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <Box 
                    sx={{ 
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: 'white'
                      }}
                    />
                  </Box>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: 'rgba(255,255,255,0.85)',
                      fontWeight: 500
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.07)',
                backdropFilter: 'blur(10px)',
                p: 4,
                borderRadius: 3,
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)',
                }}
              />
              
              <Typography 
                variant="h5"
                sx={{ 
                  color: 'white',
                  mb: 3,
                  fontWeight: 700
                }}
              >
                Sign Up Today
              </Typography>
              
              <Button 
                variant="contained" 
                color="secondary"
                size="large"
                component={RouterLink}
                to="/register"
                fullWidth
                sx={{ 
                  py: 2,
                  mb: 2,
                  borderRadius: '30px',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    bottom: '-50%',
                    left: '-50%',
                    background: 'linear-gradient(to bottom, rgba(229, 172, 142, 0), rgba(255,255,255,0.5) 50%, rgba(229, 172, 142, 0))',
                    transform: 'rotateZ(60deg) translate(-5em, 7.5em)',
                    animation: 'shine 3s infinite',
                  },
                  '@keyframes shine': {
                    '0%': {
                      transform: 'rotateZ(60deg) translate(-5em, 7.5em)'
                    },
                    '100%': {
                      transform: 'rotateZ(60deg) translate(1em, -9em)'
                    }
                  },
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 25px rgba(0,0,0,0.25)'
                  }
                }}
              >
                Register for Free
              </Button>
              
              <Button
                component={RouterLink}
                to="/login"
                color="inherit"
                sx={{ 
                  color: 'white',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Already have an account? Sign in
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
}

export default LandingPage;